import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { schoolService } from '../../services/schoolService';

interface Props {
  open: boolean;
  schoolId: string;
  onClose: () => void;
  onCreated: () => void;
}

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required').min(3, 'At least 3 characters'),
  email: yup.string().trim().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required').min(6, 'At least 6 characters'),
  phone: yup.string().trim()
});

// Used both for normal "add another admin" and for the recovery flow where a
// school never had an admin created (orphan rescue from the old non-atomic
// path).
const AddSchoolAdminDialog: React.FC<Props> = ({ open, schoolId, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik({
    initialValues: { name: '', email: '', password: '', phone: '' },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await schoolService.createAdmin(schoolId, {
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password,
          phone: values.phone.trim() || undefined
        });
        enqueueSnackbar('Admin created', { variant: 'success' });
        formik.resetForm();
        onCreated();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Failed to create admin', { variant: 'error' });
      }
    }
  });

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Add school admin</Typography>
        <Typography variant="caption" color="text.secondary">The admin will sign in with email + password.</Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              size="small" label="Name" required name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              size="small" label="Email" required type="email" name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              size="small" label="Password" required type="password" name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password ? formik.errors.password : 'At least 6 characters.'}
              autoComplete="new-password"
            />
            <TextField
              size="small" label="Phone (optional)" name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={formik.isSubmitting} color="inherit">Cancel</Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting} startIcon={formik.isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined} sx={{ borderRadius: 1.5 }}>
            {formik.isSubmitting ? 'Creating…' : 'Create Admin'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddSchoolAdminDialog;
